// Imported by the browser: keep server-only modules out.
import {
  CALENDAR_WALK_DAYS,
  isTradingDay,
  isWeekend,
  KEEP_LABEL,
  type MarketCalendar,
  nextTradingDay,
  previousTradingDay,
  VENUE_LABEL,
  VENUES,
  type Venue,
} from './calendar.ts';
import { newsOf } from './calendar-said.ts';
import {
  addDays,
  HOUR_MS,
  istAt,
  istClock,
  istMidnight,
  mondayOf,
  todayInIst,
  weekdaySaid,
} from './when.ts';

/**
 * In the order a trading day passes through them: up to its first open, while any venue trades,
 * and from its last close. A day nothing trades is `closed` throughout.
 */
export const DAY_PARTS = ['before-open', 'session', 'closed'] as const;

export type DayPart = (typeof DAY_PARTS)[number];

export const DAY_PART_LABEL: Record<DayPart, string> = {
  'before-open': 'Before the open',
  session: 'In session',
  closed: 'After the close',
};

const PREP_LEAD_HOURS = 3;

/** A day whose first open is this late (MCX evening-only while NSE is shut) is closed until its prep. */
const AFTERNOON_FROM = '12:00';

export interface DayPartSpan {
  part: DayPart;
  from: Date;
  /** Exclusive: the minute the next part starts. */
  to: Date;
}

const sessionsOn = (calendar: MarketCalendar, day: string) =>
  VENUES.flatMap((venue) => calendar.session(venue, day) ?? []);

const firstOpen = (calendar: MarketCalendar, day: string): Date | null => {
  const opens = sessionsOn(calendar, day).map((one) => one.open.getTime());
  return opens.length === 0 ? null : new Date(Math.min(...opens));
};

/** Each part runs until the next one starts, so the day is covered with no gap. */
function spansOf(calendar: MarketCalendar, day: string): DayPartSpan[] {
  const midnight = istMidnight(day);
  const end = istMidnight(addDays(day, 1));
  const first = firstOpen(calendar, day);
  if (first === null) return [{ part: 'closed', from: midnight, to: end }];
  const last = new Date(Math.max(...sessionsOn(calendar, day).map((one) => one.close.getTime())));
  const prep =
    first >= istAt(day, AFTERNOON_FROM)
      ? new Date(first.getTime() - PREP_LEAD_HOURS * HOUR_MS)
      : midnight;
  const starts: [DayPart, Date][] = [
    ['closed', midnight],
    ['before-open', prep],
    ['session', first],
    ['closed', last < end ? last : end],
  ];
  return starts.flatMap(([part, from], i) => {
    const to = starts[i + 1]?.[1] ?? end;
    return from < to ? [{ part, from, to }] : [];
  });
}

const spanAt = (spans: readonly DayPartSpan[], now: Date): DayPartSpan | undefined =>
  spans.findLast((one) => one.from <= now) ?? spans[0];

/** Phase from the calendar, never the weekday: a Budget Sunday is a trading day. */
export function dayPartOf(calendar: MarketCalendar, now: Date): DayPart {
  return spanAt(spansOf(calendar, todayInIst(now)), now)?.part ?? 'closed';
}

/** The whole run of the part `now` is in, so a weekend runs from Friday's last close to Monday. */
export function dayPartSpan(calendar: MarketCalendar, now: Date): DayPartSpan {
  const today = todayInIst(now);
  const found = spanAt(spansOf(calendar, today), now) ?? { part: 'closed', from: now, to: now };
  let { from, to } = found;
  for (let step = 1; step <= CALENDAR_WALK_DAYS; step += 1) {
    const before = spansOf(calendar, addDays(today, -step)).at(-1);
    if (before?.part !== found.part || before.to.getTime() !== from.getTime()) break;
    from = before.from;
  }
  for (let step = 1; step <= CALENDAR_WALK_DAYS; step += 1) {
    const after = spansOf(calendar, addDays(today, step))[0];
    if (after?.part !== found.part || after.from.getTime() !== to.getTime()) break;
    to = after.to;
  }
  return { part: found.part, from, to };
}

/** Nothing has traded today, nor will before the afternoon: closed, and not after a close. */
export function isDayOff(calendar: MarketCalendar, now: Date): boolean {
  const first = firstOpen(calendar, todayInIst(now));
  return first === null || (first > now && first >= istAt(todayInIst(now), AFTERNOON_FROM));
}

/**
 * A day off is said as the weekend or the holiday's name where there is one; a holiday on a
 * weekend is no news, so a Sunday holiday is still the weekend.
 */
export function dayPartSaid(calendar: MarketCalendar, now: Date): string {
  const part = dayPartOf(calendar, now);
  if (part !== 'closed' || !isDayOff(calendar, now)) return DAY_PART_LABEL[part];
  const day = todayInIst(now);
  const holiday = VENUES.map((venue) => newsOf(calendar, venue, day)).find(
    (kept) => kept !== null && kept.keep !== 'SPECIAL',
  );
  if (holiday) return holiday.name;
  return isWeekend(day) ? 'Weekend' : 'Day off';
}

const MARK_KINDS = [
  'nifty-expiry',
  'mcx-expiry',
  'half-day',
  'special',
  'last-before-off',
  'first-after-off',
] as const;

type MarkKind = (typeof MARK_KINDS)[number];

export interface Mark {
  kind: MarkKind;
  said: string;
}

/** A listed expiry as the calendar read carries it: `ContractExpiry` from the instrument dump. */
export interface ListedExpiry {
  contract: string;
  venue: Venue;
  expiry: string;
  weekly: boolean;
}

/**
 * The Monday of the week of F&O's last session on or before `day`, so a Monday holiday reads the
 * week just ended rather than one with nothing in it yet.
 */
export function weekOf(calendar: MarketCalendar, day: string): string {
  const session = isTradingDay(calendar, 'NFO', day)
    ? day
    : previousTradingDay(calendar, 'NFO', day);
  return mondayOf(session ?? day);
}

/** F&O trades today and not tomorrow, so what is held carries over a weekend or a holiday. */
export const lastBeforeOff = (calendar: MarketCalendar, day: string): boolean =>
  isTradingDay(calendar, 'NFO', day) && !isTradingDay(calendar, 'NFO', addDays(day, 1));

const firstAfterOff = (calendar: MarketCalendar, day: string): boolean =>
  isTradingDay(calendar, 'NFO', day) && !isTradingDay(calendar, 'NFO', addDays(day, -1));

function expiryMarks(expiries: readonly ListedExpiry[], today: string): Mark[] {
  const due = expiries.filter((one) => one.expiry === today);
  const names = (list: readonly ListedExpiry[]): string =>
    [...new Set(list.map((one) => one.contract))].join(', ');
  const nifty = [false, true].flatMap((weekly) => {
    const listed = due.filter((one) => one.venue === 'NFO' && one.weekly === weekly);
    if (listed.length === 0) return [];
    const said = `${names(listed)} ${weekly ? 'weekly' : 'monthly'} expiry`;
    return [{ kind: 'nifty-expiry' as const, said }];
  });
  const mcx = due.filter((one) => one.venue === 'MCX');
  return [
    ...nifty,
    ...(mcx.length === 0 ? [] : [{ kind: 'mcx-expiry' as const, said: `${names(mcx)} expiry` }]),
  ];
}

/** The venue chips carry the hours; a mark names what is unusual about them. */
function keptMarks(calendar: MarketCalendar, today: string): Mark[] {
  const half: Mark[] = [];
  const special = new Map<string, boolean>();
  for (const venue of VENUES) {
    const kept = calendar.kept(venue, today);
    const session = calendar.session(venue, today);
    if (kept?.keep === 'SPECIAL') {
      special.set(kept.name, (special.get(kept.name) ?? false) || session !== null);
    }
    if (session === null || (kept?.keep !== 'EVENING_ONLY' && kept?.keep !== 'MORNING_ONLY')) {
      continue;
    }
    const bound =
      kept.keep === 'EVENING_ONLY'
        ? `from ${istClock(session.open)}`
        : `to ${istClock(session.close)}`;
    half.push({
      kind: 'half-day',
      said: `${VENUE_LABEL[venue]} ${KEEP_LABEL[kept.keep].toLowerCase()} ${bound} — ${kept.name}`,
    });
  }
  return [
    ...half,
    ...[...special].map(([name, announced]) => ({
      kind: 'special' as const,
      said: `${name} · special session${announced ? '' : ', hours not yet announced'}`,
    })),
  ];
}

/** What makes today unlike an ordinary one, in `MARK_KINDS` order. */
export function dayMarks(
  calendar: MarketCalendar,
  expiries: readonly ListedExpiry[],
  today: string,
): Mark[] {
  const marks = [...expiryMarks(expiries, today), ...keptMarks(calendar, today)];
  const resumes = lastBeforeOff(calendar, today) ? nextTradingDay(calendar, 'NFO', today) : null;
  if (resumes !== null) {
    marks.push({ kind: 'last-before-off', said: `Last F&O session until ${weekdaySaid(resumes)}` });
  }
  const since = firstAfterOff(calendar, today) ? previousTradingDay(calendar, 'NFO', today) : null;
  if (since !== null) {
    marks.push({ kind: 'first-after-off', said: `First F&O session since ${weekdaySaid(since)}` });
  }
  return marks;
}
